import { useSyncExternalStore } from "react";
import { apiClient, ApiError } from "../../api/client";

// The Admin's "View as role" (owner, 2026-10-22, D-O115). The server issues a signed, one-hour session; this module keeps it
// for this browser tab (sessionStorage, like the persona and the Microsoft sign-in) and sends it on every API call in the
// X-Summit-View-As header. The server narrows the request to that role and refuses every change, so this file decides
// nothing about permissions: it only carries the session and shows it.

export const VIEW_AS_HEADER = "X-Summit-View-As";
export const VIEW_AS_PROBLEM = "https://summit-aviation.com/problems/view-as-read-only";
const KEY = "summitops.viewAs";

export interface ViewAsSession {
  token: string;
  role: string;
  roleLabel: string;
  startedAtUtc: string;
  expiresAtUtc: string;
}

export interface ViewAsRoleOption {
  role: string;
  label: string;
}

export interface ViewAsStatus {
  viewing: boolean;
  role: string | null;
  roleLabel: string | null;
  startedAtUtc: string | null;
  expiresAtUtc: string | null;
  canViewAs: boolean;
  ended: boolean;
  roles: ViewAsRoleOption[];
}

export interface TourChapterResponse {
  primary: string | null;
  others: string[];
}

const listeners = new Set<() => void>();
let cached: ViewAsSession | null | undefined;

function load(): ViewAsSession | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<ViewAsSession>;
    if (typeof s.token !== "string" || typeof s.role !== "string" || typeof s.expiresAtUtc !== "string") return null;
    return {
      token: s.token,
      role: s.role,
      roleLabel: typeof s.roleLabel === "string" ? s.roleLabel : s.role,
      startedAtUtc: typeof s.startedAtUtc === "string" ? s.startedAtUtc : "",
      expiresAtUtc: s.expiresAtUtc
    };
  } catch {
    return null;
  }
}

/** The current view-as session in this tab, or null. An expired one is still returned so "Back to Admin" can be audited. */
export function readViewAs(): ViewAsSession | null {
  if (cached === undefined) cached = load();
  return cached;
}

function emit() {
  listeners.forEach((l) => l());
}

export function setViewAs(session: ViewAsSession): void {
  cached = session;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    // Storage can be unavailable; the session still lasts for this page.
  }
  emit();
}

export function clearViewAs(): void {
  cached = null;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // nothing to remove
  }
  emit();
}

/** True once the session's hour is up. */
export function viewAsExpired(session: ViewAsSession, now: number = Date.now()): boolean {
  const end = Date.parse(session.expiresAtUtc);
  return Number.isFinite(end) && now >= end;
}

export function useViewAs(): ViewAsSession | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    readViewAs,
    readViewAs
  );
}

/** The banner's words: "Viewing as Purchaser: read-only demo". */
export function viewAsBannerText(roleLabel: string): string {
  return `Viewing as ${roleLabel}: read-only demo`;
}

// Every API call carries the session (registered once, when this module is first imported by the header).
apiClient.interceptors.request.use((config) => {
  const s = readViewAs();
  if (s && !config.headers.has(VIEW_AS_HEADER)) config.headers.set(VIEW_AS_HEADER, s.token);
  return config;
});

// A refusal while viewing reads as the owner's sentence, not as "your role does not include access".
apiClient.interceptors.response.use(undefined, (error: unknown) => {
  if (error instanceof ApiError && error.type === VIEW_AS_PROBLEM) {
    const detail = error.message;
    if (error.extensions.viewAsRole === undefined && readViewAs()) clearViewAs();
    return Promise.reject(new ApiError(error.status, detail, detail, error.correlationId, error.extensions));
  }
  return Promise.reject(error);
});

export const viewAsApi = {
  status: async (): Promise<ViewAsStatus> => (await apiClient.get<ViewAsStatus>("/view-as")).data,
  start: async (role: string): Promise<ViewAsSession> => (await apiClient.post<ViewAsSession>("/view-as", { role })).data,
  stop: async (): Promise<{ stopped: boolean; role: string | null }> => (await apiClient.post<{ stopped: boolean; role: string | null }>("/view-as/stop")).data,
  tourChapter: async (): Promise<TourChapterResponse> => (await apiClient.get<TourChapterResponse>("/tour/chapter")).data
};

/** "Back to Admin": tells the server (audited), then forgets the session in this tab, whatever the answer. */
export async function stopViewAs(): Promise<void> {
  if (!readViewAs()) return;
  try {
    await viewAsApi.stop();
  } catch {
    // The session ends here anyway; the server ignores it after the hour.
  } finally {
    clearViewAs();
  }
}
